import { Container } from "pixi.js";

import { FX } from "../../../shared/Config";
import { MUZZLE_PREFIX, muzzleFlashOf, pickMuzzleFlash } from "../../../shared/Fx";
import { Bursts, effectsUnder, rollFor } from "../fx/Bursts";

/**
 * The fire out of a gun: one short animation off the `fxA` sheet, pinned to the muzzle.
 *
 * One of these belongs to each boat, and everything it draws is a child of that boat — so a
 * flash rides the hull that made it for the third of a second it lives, rather than blooming at a
 * world coordinate the ship has already sailed out of. At cruising speed a hull covers about
 * sixty pixels in that time, which is a third of its own length, so the difference is not subtle.
 *
 * **Which flash comes out is a weighted draw**, and the roll is seeded off the shot's own id
 * rather than off `Math.random`. That costs nothing and buys two things: every client watching
 * the same broadside sees the same flame, and the choice is reproducible. Nothing about it goes
 * on the wire, because nothing needs to — see design/Networking.md on what the arena owns.
 *
 * **The art the sheet holds is what exists**, exactly as in the dashboard: the sheet's animations
 * are the roster and `config/fx.json` only says how each is fitted. Art dropped into the sheet
 * joins the rotation at the default weight without anything here being edited. See shared/Fx.ts.
 *
 * The pool, the clock and the frame stepping are [Bursts](../fx/Bursts.ts)'; what is left here is
 * everything that makes a flash a flash rather than any other effect.
 */

/**
 * How far back down the barrel a flash sits from the ball's own spawn point, as a fraction of
 * the gun's drawn length.
 *
 * Zero, and deliberately so: the ball and the fire come out of the same hole, which is the whole
 * argument for the flash being pinned by its pivot to the authored spawn point rather than to a
 * second point somebody has to keep in step with the first. It is named here because that is a
 * decision rather than an absence — if a flash ever needs to sit proud of the muzzle, this is the
 * line to change, not `CannonType.muzzleY`, which the arena fires from.
 */
const MUZZLE_INSET = 0;

export class MuzzleFlashes {
    private readonly _bursts = new Bursts();

    /** Add this to the boat, over the deck: the fire is in front of the barrel that made it. */
    public get container(): Container {
        return this._bursts.container;
    }

    /**
     * Lights one flash at a gun's muzzle, in the **boat's own** space — the caller has already
     * turned the gun's offsets into deck coordinates, because it is the thing that knows where
     * the gun has swung to.
     *
     * `gunLength` is the gun as it is actually drawn (`Cannon.drawn`), which is the unit
     * `MuzzleFlash.scale` counts in. `seed` is the shot's id.
     *
     * Does nothing at all when the sheet holds no flashes, or when every weight is zero — both
     * are authored states rather than failures. See `pickMuzzleFlash`.
     */
    public fire(x: number, y: number, bearing: number, gunLength: number, seed: number): void {
        const name = pickMuzzleFlash(FX, effectsUnder(MUZZLE_PREFIX), rollFor(seed));
        if (!name) return;

        const flash = muzzleFlashOf(FX, name);

        this._bursts.spawn(
            name,
            // Back down the barrel from the ball's own spawn point. Zero today — see MUZZLE_INSET.
            x + Math.sin(bearing) * MUZZLE_INSET * gunLength,
            y - Math.cos(bearing) * MUZZLE_INSET * gunLength,
            // The gun's bearing, plus however far round the art itself has to be turned to point
            // up the barrel. Degrees in the tree, radians here — the one place that conversion
            // happens for this field, exactly as `rosterFor` does it for a gun's traverse.
            bearing + (flash.rotation * Math.PI) / 180,
            flash.scale * gunLength,
            flash.fps,
            // The pivot the artist authored on the frame: a flash grows out of the barrel rather
            // than around its own centre, and its frames are not registered to their own middles.
            "anchor"
        );
    }

    public update(deltaSeconds: number): void {
        this._bursts.update(deltaSeconds);
    }

    public destroy(): void {
        this._bursts.destroy();
    }
}
